import { useState, useContext, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import starIcon from '../../../assets/dashboard_assets/star.png';
import AuthContext from '../../../context/AuthContext';
import { createRazorpayOrder, verifyRazorpayPayment, loadRazorpayScript } from '../../../services/paymentApi';
import { fetchDiscountEligibility } from '../../../services/referralsApi';

const MIN_LISTINGS = 3;
const MAX_LISTINGS = 300;
const MRP = 400;
const CREDITS_PER_LISTING = 50;
const GST_RATE = 0.18;
// Backend applies the same percent atomically via reserve_discount; kept in
// sync with REFEREE_DISCOUNT_PERCENT in backend-mvp/app/referrals/service.py.
const REFERRAL_DISCOUNT_PERCENT = 10;

const shapeCouponForUI = (c, subtotal, isBestDeal) => {
    const value = Number(c.benefit_value) || 0;
    let offLabel, save;
    switch (c.benefit_type) {
        case 'percent_off': {
            const raw = subtotal * value / 100;
            const capped = c.max_discount_cap != null ? Math.min(raw, c.max_discount_cap) : raw;
            offLabel = `${value}% Off`;
            save = Math.round(capped);
            break;
        }
        case 'flat_off':
            offLabel = `₹${value} Off`;
            save = Math.min(value, subtotal);
            break;
        case 'bonus_credits_flat':
            offLabel = `+${value} credits`;
            save = 0;
            break;
        case 'bonus_credits_percent':
            offLabel = `+${value}% credits`;
            save = 0;
            break;
        default:
            offLabel = c.benefit_type;
            save = 0;
    }
    const parts = [];
    if (c.min_purchase_amount) parts.push(`Valid on packs of ₹${c.min_purchase_amount.toLocaleString('en-IN')} and above.`);
    if (c.min_credits) parts.push(`Requires at least ${c.min_credits} credits in cart.`);
    if (c.first_purchase_only) parts.push('First purchase only.');
    const condition = parts.join(' ') || (c.description || '');
    return { code: c.code, offLabel, save, condition, bestDeal: isBestDeal };
};

const getPerListing = (count) => {
    if (count <= 10) return 260;
    if (count <= 50) return 250;
    if (count <= 100) return 235;
    if (count <= 150) return 220;
    if (count <= 200) return 210;
    return 200;
};


const SummaryRow = ({ label, value, valueClassName }) => (
    <div className="w-full flex justify-between items-center">
        <span className="text-[#737373] text-sm font-medium leading-6 font-sans">{label}</span>
        <span className={valueClassName || 'text-[#737373] text-sm font-medium leading-6 font-sans'}>{value}</span>
    </div>
);

const CreditSlider = ({ presetListings, onPresetConsumed }) => {
    const [searchParams] = useSearchParams();
    const authCtx = useContext(AuthContext);
    const initialCredits = Math.min(MAX_LISTINGS, Math.max(MIN_LISTINGS, Number(searchParams.get('credits')) || 8));
    const [listings, setListings] = useState(initialCredits);
    const [inputText, setInputText] = useState(String(initialCredits));

    // When Buy Pack sets presetListings, update slider
    useEffect(() => {
        if (presetListings && presetListings >= MIN_LISTINGS && presetListings <= MAX_LISTINGS) {
            updateListings(presetListings);
            if (onPresetConsumed) onPresetConsumed();
        }
    }, [presetListings]);
    const [inputShake, setInputShake] = useState(false);
    const [paying, setPaying] = useState(false);
    const [billingForm, setBillingForm] = useState(null); // null = not loaded, {} = no address
    const [useDefault, setUseDefault] = useState(true);
    const [newAddr, setNewAddr] = useState({ company_name: '', gstin: '', address_line1: '', address_line2: '', city: '', state: '', pincode: '', country: 'India' });
    const [setAsDefault, setSetAsDefault] = useState(true);
    const [showBilling, setShowBilling] = useState(false);
    const [coupon, setCoupon] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState('');
    const [showCoupons, setShowCoupons] = useState(false);
    const [availableCoupons, setAvailableCoupons] = useState([]);
    // Backend-validated benefit for whatever code the user last applied.
    // Populated by /api/coupons/validate — carries the discount, bonus, etc.
    // Cleared whenever the code changes or the cart is invalidated.
    const [validatedBenefit, setValidatedBenefit] = useState(null);
    const [couponError, setCouponError] = useState('');
    const [validatingCoupon, setValidatingCoupon] = useState(false);
    // True iff this user was referred and hasn't used their one-time discount.
    // Drives the "Referral Discount (10%)" line in the Order Summary.
    const [referralEligible, setReferralEligible] = useState(false);
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

    // Check once per session whether the referred-user discount applies.
    // The backend re-verifies + atomically claims the slot at order creation,
    // so a stale positive here just means the user sees the discount and the
    // backend either honours or silently drops it — no double-charge risk.
    const refetchReferralEligibility = useCallback(() => {
        if (!authCtx.token) return Promise.resolve();
        // Match the purchase_type sent by createRazorpayOrder below so the
        // eligibility check and the actual reservation use the same key.
        return fetchDiscountEligibility(authCtx.token, 'token_pack')
            .then(data => setReferralEligible(!!data.eligible))
            .catch(() => setReferralEligible(false));
    }, [authCtx.token]);

    useEffect(() => {
        refetchReferralEligibility();
    }, [refetchReferralEligibility]);

    // Callable so we can re-fetch after a payment success (the just-used
    // coupon should drop out of the list per per_user_limit rules).
    const fetchAvailableCoupons = useCallback(() => {
        if (!authCtx.token) return Promise.resolve();
        return fetch(`${API_URL}/coupons/available`, {
            headers: { 'Authorization': `Bearer ${authCtx.token}` },
        })
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then(data => setAvailableCoupons(data.coupons || []))
            .catch(() => setAvailableCoupons([]));
    }, [authCtx.token, API_URL]);

    useEffect(() => {
        fetchAvailableCoupons();
    }, [fetchAvailableCoupons]);

    // Load billing address from profile
    useState(() => {
        if (!authCtx.token) return;
        fetch(`${API_URL}/auth/billing-address`, { headers: { 'Authorization': `Bearer ${authCtx.token}` } })
            .then(r => r.json())
            .then(data => {
                if (data.success && data.billing_address?.address_line1) {
                    setBillingForm(data.billing_address);
                } else {
                    setBillingForm({});
                    setUseDefault(false);
                }
            })
            .catch(() => { setBillingForm({}); setUseDefault(false); });
    }, [authCtx.token]);

    const activeBilling = useDefault && billingForm?.address_line1 ? billingForm : newAddr;
    const hasBillingAddr = activeBilling.address_line1 && activeBilling.city && activeBilling.state && activeBilling.pincode;

    // Auto-save new address to profile
    const saveNewAddrToProfile = async (addr) => {
        try {
            await fetch(`${API_URL}/auth/billing-address`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${authCtx.token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(addr),
            });
        } catch {}
    };

    const handlePayment = async () => {
        if (!authCtx.token || paying) return;
        if (!hasBillingAddr) { alert('Please fill billing address'); return; }
        // If new address and user wants to save as default
        if (!useDefault && newAddr.address_line1 && setAsDefault) {
            saveNewAddrToProfile(newAddr);
            setBillingForm(newAddr); // update local cache
        }
        setPaying(true);
        try {
            const credits = listings * CREDITS_PER_LISTING;
            const subtotalAtPay = listings * getPerListing(listings);
            const couponSave = activeCoupon ? Math.min(activeCoupon.save || 0, subtotalAtPay) : 0;
            // Backend recomputes referral discount + GST from meta.subtotal
            // and meta.coupon_discount_amount — the values sent here are the
            // pre-referral fallback used if reserve_discount fails.
            const preReferralTaxableAtPay = Math.max(subtotalAtPay - couponSave, 0);
            const gstAtPay = Math.round(preReferralTaxableAtPay * GST_RATE);
            const amount = preReferralTaxableAtPay + gstAtPay;
            const mrp = listings * MRP;
            const discount = (mrp - subtotalAtPay) + couponSave;
            const discountPctVal = Math.round(((MRP - getPerListing(listings)) / MRP) * 100);
            const orderRes = await createRazorpayOrder(authCtx.token, amount, 'token_pack', {
                credits,
                subtotal: subtotalAtPay,
                gst: gstAtPay,
                mrp,
                discount,
                discount_pct: discountPctVal,
                customer_gstin: activeBilling.gstin || undefined,
                billing_address: hasBillingAddr ? activeBilling : undefined,
                customer_email: (authCtx.user || {}).email || '',
                customer_phone: (authCtx.user || {}).phonenumber || '',
                ...(activeCoupon ? { coupon_code: activeCoupon.code, coupon_discount: couponSave } : {}),
            }, activeCoupon ? activeCoupon.code : null);
            if (!orderRes.success) { alert(orderRes.message || 'Order failed'); return; }

            const isLoaded = await loadRazorpayScript();
            if (!isLoaded) { alert('Payment gateway failed to load'); return; }

            const user = authCtx.user || {};
            // If the user dismisses / fails the Razorpay modal, free up both
            // the coupon and referral reservations immediately so they can
            // retry without waiting for the 20-min TTL to expire on either.
            const releaseReservation = () => {
                if (activeCoupon) {
                    fetch(`${API_URL}/coupons/release-reservation`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${authCtx.token}`,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ code: activeCoupon.code, order_id: orderRes.order.id }),
                    }).catch(() => {});
                }
                if (referralEligible) {
                    fetch(`${API_URL}/referrals/release-reservation`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${authCtx.token}`,
                            'Content-Type': 'application/json',
                        },
                    }).catch(() => {});
                }
            };
            const rzp = new window.Razorpay({
                key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                amount: orderRes.order.amount,
                currency: 'INR',
                order_id: orderRes.order.id,
                name: 'EcomBuddha',
                description: `${credits} Credits - Pay As You Go`,
                image: '/favicon.png',
                notify: { email: false, sms: false },
                prefill: {
                    name: user.fullname || user.username || '',
                    email: user.email || '',
                    contact: user.phonenumber || '',
                },
                handler: async (response) => {
                    try {
                        const res = await verifyRazorpayPayment(authCtx.token, response.razorpay_order_id, response.razorpay_payment_id, response.razorpay_signature);
                        if (res.success && authCtx.updateUserCredit && res.user_balance) {
                            authCtx.updateUserCredit(res.user_balance.credits);
                            // Reset form — including the applied coupon, so the
                            // next purchase starts from a clean state (else the
                            // stale code stays visible + would re-trigger
                            // "already used" errors on the next validate call).
                            updateListings(8);
                            setUseDefault(true);
                            setNewAddr({ company_name: '', gstin: '', address_line1: '', address_line2: '', city: '', state: '', pincode: '', country: 'India' });
                            clearCoupon();
                            setShowBilling(false);
                            // Re-fetch offers so a just-consumed one-time coupon
                            // drops out of the "Available Coupons" list.
                            fetchAvailableCoupons();
                            // First paid purchase burns the referral discount —
                            // refetch so the 10% line disappears immediately.
                            refetchReferralEligibility();
                        } else { releaseReservation(); alert('Payment verification failed.'); }
                    } catch { releaseReservation(); alert('Payment verification error.'); }
                },
                theme: { color: '#2766AA', backdrop_color: 'rgba(39, 102, 170, 0.15)' },
                modal: { ondismiss: releaseReservation },
            });
            rzp.on('payment.failed', releaseReservation);
            rzp.open();
        } catch (err) { alert(err.message || 'Something went wrong'); }
        finally { setPaying(false); }
    };

    const perListing = getPerListing(listings);
    const credits = listings * CREDITS_PER_LISTING;
    const subtotal = listings * perListing;
    const mrpTotal = listings * MRP;
    const savings = mrpTotal - subtotal;
    const discountPct = Math.round(((MRP - perListing) / MRP) * 100);
    // Backend returns coupons sorted best-first; the first eligible one is the "best deal".
    const shapedCoupons = availableCoupons.map((c, i) => shapeCouponForUI(c, subtotal, i === 0));
    // The backend-validated benefit wins over any list-based match — it's the
    // only source of truth for private / specific-account codes that never
    // show up in /available. Reuse shapeCouponForUI so the "10% Off" label
    // format is identical to the offer cards.
    const activeCoupon = (validatedBenefit && validatedBenefit.code === appliedCoupon)
        ? shapeCouponForUI(validatedBenefit, subtotal, false)
        : shapedCoupons.find(c => c.code === appliedCoupon);
    const couponDiscount = activeCoupon ? Math.min(activeCoupon.save || 0, subtotal) : 0;
    // Pre-GST math: 10% referral is subtracted from the taxable base before
    // GST, so GST also reduces. Matches backend apply_discount() in
    // backend-mvp/app/referrals/service.py — keep in sync.
    const preReferralTaxable = Math.max(subtotal - couponDiscount, 0);
    const referralDiscount = referralEligible
        ? Math.round(preReferralTaxable * REFERRAL_DISCOUNT_PERCENT / 100)
        : 0;
    const taxableAmount = preReferralTaxable - referralDiscount;
    const gst = Math.round(taxableAmount * GST_RATE);
    const total = taxableAmount + gst;
    const totalSavings = savings + couponDiscount + referralDiscount;
    const perCredit = (subtotal / credits).toFixed(2);
    const sliderProgress = ((listings - MIN_LISTINGS) / (MAX_LISTINGS - MIN_LISTINGS)) * 100;
    const formatCurrency = (amount) => `₹${amount.toLocaleString('en-IN')}`;

    const updateListings = (v) => {
        setListings(v);
        setInputText(String(v));
    };

    const clearCoupon = () => {
        setCoupon('');
        setAppliedCoupon('');
        setValidatedBenefit(null);
        setCouponError('');
    };

    // Debounced re-validation when the cart changes after a coupon was applied.
    // Slider drags used to fire one call per tick and blew through the backend
    // rate limit — waiting 500 ms after the user settles avoids that.
    useEffect(() => {
        if (!appliedCoupon || !validatedBenefit) return;
        const t = setTimeout(() => applyCouponCode(appliedCoupon), 500);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listings]);

    // Calls backend /api/coupons/validate so that private codes and
    // specific-account eligibility (which /available filters out) still
    // resolve correctly. On success the discount lands in state and the
    // Order Summary updates; on failure we surface the exact backend error.
    const applyCouponCode = async (rawCode) => {
        const code = (rawCode || '').trim().toUpperCase();
        if (!code || !authCtx.token) return;
        setValidatingCoupon(true);
        setCouponError('');
        try {
            const res = await fetch(`${API_URL}/coupons/validate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authCtx.token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ code, subtotal, credits }),
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                setAppliedCoupon('');
                setValidatedBenefit(null);
                setCouponError(data.error || 'This coupon code is not valid.');
                return;
            }
            setAppliedCoupon(data.code);
            setCoupon(data.code);
            setValidatedBenefit(data);
            setCouponError('');
        } catch (err) {
            setCouponError('Could not validate coupon. Please try again.');
        } finally {
            setValidatingCoupon(false);
        }
    };

    return (
        <div className="w-full">
            <div className="flex items-stretch gap-4">
                {/* Slider Card */}
                <div className="flex-1 bg-[#FEFEFF] shadow-[4px_46px_134px_14px_rgba(96,146,200,0.10)] rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#D0E7FF] px-5 py-4 flex flex-col justify-between gap-3">
                    <div className="flex flex-col gap-1">
                        <div className="text-[#505050] text-lg font-semibold font-heading">Customise your credit pack</div>
                        <span className="text-[#898989] text-sm font-medium">Choose the exact number of listings you need.</span>
                    </div>

                    {/* Listings input + credits display */}
                    <div className="flex items-center justify-between gap-2.5">
                        <div className="flex items-center gap-2.5">
                            <div className="text-[#898989] text-xs font-semibold uppercase tracking-wide">Listings :</div>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => updateListings(Math.max(MIN_LISTINGS, listings - 1))}
                                    disabled={listings <= MIN_LISTINGS}
                                    className="w-7 h-7 flex items-center justify-center text-[#2766AA] bg-[#EEF5FF] rounded-md hover:bg-[#D9EAFF] disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-lg font-semibold leading-none"
                                    aria-label="Decrease listings"
                                >
                                    −
                                </button>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={inputText}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(/\D/g, '');
                                        setInputText(raw);
                                        if (raw === '') return;
                                        const num = Number(raw);
                                        if (num >= MIN_LISTINGS) {
                                            setListings(Math.min(MAX_LISTINGS, num));
                                        }
                                    }}
                                    onBlur={() => {
                                        const num = Number(inputText);
                                        if (!inputText || num < MIN_LISTINGS) {
                                            setInputShake(true);
                                            setTimeout(() => setInputShake(false), 500);
                                            updateListings(MIN_LISTINGS);
                                        } else {
                                            updateListings(Math.min(MAX_LISTINGS, num));
                                        }
                                    }}
                                    className={`w-[48px] text-[#2766AA] text-base font-semibold bg-[#EEF5FF] outline-none text-center rounded-md py-1 focus:ring-1 focus:ring-[#78B3F3] transition-all ${inputShake ? 'ring-1 ring-red-400 bg-red-50' : ''}`}
                                    style={inputShake ? { animation: 'headShake 0.5s ease' } : {}}
                                />
                                <button
                                    type="button"
                                    onClick={() => updateListings(Math.min(MAX_LISTINGS, listings + 1))}
                                    disabled={listings >= MAX_LISTINGS}
                                    className="w-7 h-7 flex items-center justify-center text-[#2766AA] bg-[#EEF5FF] rounded-md hover:bg-[#D9EAFF] disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-lg font-semibold leading-none"
                                    aria-label="Increase listings"
                                >
                                    +
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <img src={starIcon} alt="credits" className="w-4 h-4" />
                            <span className="text-[#2766AA] text-xs font-semibold uppercase tracking-wide">{credits.toLocaleString()} Credits</span>
                        </div>
                    </div>

                    {/* Slider */}
                    <div className="flex flex-col gap-3">
                        <style>{`
                            .listings-slider {
                                -webkit-appearance: none;
                                appearance: none;
                                width: 100%;
                                height: 10px;
                                border-radius: 40px;
                                background: linear-gradient(to right, #78B3F3 ${sliderProgress}%, #E6E4F1 ${sliderProgress}%);
                                outline: none;
                                cursor: pointer;
                            }
                            .listings-slider::-webkit-slider-thumb {
                                -webkit-appearance: none;
                                appearance: none;
                                width: 22px;
                                height: 22px;
                                border-radius: 9999px;
                                background: #78B3F3;
                                cursor: pointer;
                                box-shadow: 0 2px 6px rgba(0,0,0,0.15);
                            }
                            .listings-slider::-moz-range-thumb {
                                width: 22px;
                                height: 22px;
                                border-radius: 9999px;
                                background: #78B3F3;
                                cursor: pointer;
                                border: none;
                                box-shadow: 0 2px 6px rgba(0,0,0,0.15);
                            }
                            .listings-slider::-moz-range-track {
                                height: 10px;
                                border-radius: 40px;
                                background: transparent;
                            }
                            @keyframes headShake {
                                0% { transform: translateX(0); }
                                15% { transform: translateX(-4px); }
                                30% { transform: translateX(4px); }
                                45% { transform: translateX(-3px); }
                                60% { transform: translateX(3px); }
                                75% { transform: translateX(-1px); }
                                100% { transform: translateX(0); }
                            }
                        `}</style>
                        <input
                            type="range"
                            min={MIN_LISTINGS}
                            max={MAX_LISTINGS}
                            step="1"
                            value={listings}
                            onChange={(e) => updateListings(Number(e.target.value))}
                            className="listings-slider"
                            aria-label="Number of listings"
                        />
                        <div className="w-full flex justify-between text-[#898989] text-xs font-semibold">
                            {[MIN_LISTINGS, 100, 200, MAX_LISTINGS].map((tick) => (
                                <button
                                    key={tick}
                                    type="button"
                                    onClick={() => updateListings(tick)}
                                    className={`transition-colors bg-transparent border-none cursor-pointer p-0 font-semibold text-xs ${listings === tick ? 'text-[#2766AA]' : 'text-[#898989] hover:text-[#2766AA]'}`}
                                >
                                    {tick}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Per listing price */}
                    <div className="flex items-baseline gap-2.5 pt-1">
                        <span className="text-[#1F1F1F] text-[24px] font-bold leading-tight">₹{perListing}</span>
                        <span className="text-[#737373] text-base font-medium line-through">₹{MRP}</span>
                        <span className="text-[#737373] text-sm font-medium">/listing</span>
                        <span className="text-[#2E9E5C] text-sm font-bold">{discountPct}% off</span>
                    </div>

                    {/* What you get with these credits */}
                    <div className="flex flex-col gap-2 pt-2 border-t border-[#E8F0FE]">
                        <span className="text-[#505050] text-sm font-semibold font-sans">What you get with {credits.toLocaleString()} credits</span>
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                { label: 'Listings', count: Math.floor(credits / 50) },
                                { label: 'Listing Score Checks', count: Math.floor(credits / 10) },
                                { label: 'Deal Tag Checks', count: credits },
                                { label: 'Catalogue Health Fixes', count: credits },
                            ].map((item) => (
                                <div key={item.label} className="flex flex-col gap-0.5 px-3 py-2 bg-[#F8FBFF] rounded-lg">
                                    <span className="text-[#2766AA] text-lg font-bold font-sans">Up to {item.count.toLocaleString()}</span>
                                    <span className="text-[#737373] text-xs font-medium font-sans">{item.label}</span>
                                </div>
                            ))}
                        </div>
                        <span className="text-[#898989] text-xs font-medium font-sans">Credits never expire · Actual usage varies based on features used and listing variations</span>
                    </div>
                </div>

                {/* Order Summary Card */}
                <div className="w-[380px] bg-[#FEFEFF] shadow-[4px_46px_134px_14px_rgba(96,146,200,0.10)] rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#D0E7FF] px-5 py-4 flex flex-col gap-3">
                    <h3 className="text-[#505050] text-lg font-semibold font-sans">Order Summary</h3>
                    <div className="w-full flex flex-col gap-3">
                        <SummaryRow label="Credits" value={credits.toLocaleString()} />
                        <SummaryRow label="Per credit" value={`₹${perCredit}`} />
                        <SummaryRow label="Subtotal" value={formatCurrency(mrpTotal)} valueClassName="text-[#505050] text-sm font-semibold font-sans" />
                        <div className="w-full flex justify-between items-center">
                            <span className="text-[#2E9E5C] text-sm font-semibold font-sans">Discount ({discountPct}%)</span>
                            <span className="text-[#2E9E5C] text-sm font-semibold font-sans">-{formatCurrency(savings)}</span>
                        </div>
                        {activeCoupon && (
                            <div className="w-full flex justify-between items-center">
                                <span className="text-[#2E9E5C] text-sm font-semibold font-sans">
                                    Coupon ({activeCoupon.code}
                                    {activeCoupon.offLabel ? ` · ${activeCoupon.offLabel}` : ''})
                                </span>
                                <span className="text-[#2E9E5C] text-sm font-semibold font-sans">-{formatCurrency(activeCoupon.save)}</span>
                            </div>
                        )}
                        {referralEligible && referralDiscount > 0 && (
                            <div className="w-full flex justify-between items-center">
                                <span className="text-[#2E9E5C] text-sm font-semibold font-sans">Referral Discount ({REFERRAL_DISCOUNT_PERCENT}%)</span>
                                <span className="text-[#2E9E5C] text-sm font-semibold font-sans">-{formatCurrency(referralDiscount)}</span>
                            </div>
                        )}
                        <SummaryRow label="GST (18%)" value={formatCurrency(gst)} />
                        <div className="w-full h-px bg-[#D0E7FF]" />
                        <div className="w-full flex justify-between items-center">
                            <span className="text-[#505050] text-base font-semibold leading-6 font-sans">Total</span>
                            <span className="text-[#505050] text-xl font-bold font-sans">{formatCurrency(total)}</span>
                        </div>
                        <div className="w-full py-2 px-3 bg-[#E8F7ED] rounded-lg flex justify-center items-center">
                            {(couponDiscount > 0 || referralDiscount > 0) ? (
                                <span className="text-[#2E9E5C] text-sm font-bold font-sans">You save {formatCurrency(totalSavings)}</span>
                            ) : (
                                <span className="text-[#2E9E5C] text-sm font-bold font-sans">You save {formatCurrency(savings)} ({discountPct}% off)</span>
                            )}
                        </div>
                        {/* Coupon */}
                        <div className="w-full flex flex-col items-end gap-3">
                            <div className="w-full flex items-stretch gap-2">
                                <input
                                    type="text"
                                    value={coupon}
                                    onChange={(e) => { setCoupon(e.target.value); setCouponError(''); }}
                                    placeholder="Enter Coupon Code"
                                    disabled={!!activeCoupon}
                                    className={`flex-1 min-w-0 h-12 px-4 rounded-lg outline outline-1 outline-offset-[-1px] shadow-[0px_0px_134px_14px_rgba(120,179,243,0.07)] text-base font-medium font-sans placeholder:text-[#898989] focus:outline-[#2766AA] ${
                                        activeCoupon
                                            ? 'bg-[#F2F5F8] outline-[#E0E5EA] text-[#8B8B8B] cursor-not-allowed'
                                            : 'bg-white/50 outline-[#D0E7FF] text-[#505050]'
                                    }`}
                                />
                                {activeCoupon ? (
                                    <button
                                        type="button"
                                        onClick={clearCoupon}
                                        title="Click to remove"
                                        className="h-12 px-4 rounded-lg outline outline-[1.5px] outline-offset-[-1.5px] outline-[#E0E5EA] bg-[#F2F5F8] flex justify-center items-center gap-1 text-[#8B8B8B] text-base font-semibold font-sans cursor-pointer hover:bg-[#E9EDF1] hover:text-[#5A5A5A] transition-colors"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                                            <path d="M4 10.5 8 14 16 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        Applied
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        disabled={!coupon.trim() || validatingCoupon}
                                        onClick={() => applyCouponCode(coupon)}
                                        className="h-12 px-4 rounded-lg outline outline-[1.5px] outline-offset-[-1.5px] outline-[#2766AA] flex justify-center items-center text-[#2766AA] text-base font-semibold font-sans transition-opacity duration-200 disabled:opacity-50 enabled:hover:opacity-90 enabled:cursor-pointer"
                                    >
                                        Apply
                                    </button>
                                )}
                            </div>
                            {couponError && (
                                <div className="w-full text-[#B00020] text-xs font-medium font-sans">{couponError}</div>
                            )}
                            <button type="button" onClick={() => setShowCoupons(true)} className="text-[#2766AA] text-sm font-semibold font-sans hover:underline cursor-pointer">
                                View all coupons
                            </button>
                        </div>
                    </div>
                    <button onClick={() => setShowBilling(true)} className="w-full h-12 px-5 bg-gradient-to-r from-[#2766AA] to-[#0752B0] rounded-xl flex justify-center items-center cursor-pointer hover:opacity-90 transition-opacity duration-200">
                        <span className="text-white text-base font-semibold font-sans">Buy Credits</span>
                    </button>
                    <span className="text-[#737373] text-xs font-medium font-sans">
                        Prices inclusive of 18% GST. One-time charge, no recurring billing.
                    </span>
                </div>
            </div>

            {showBilling && createPortal(
                <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" role="dialog" aria-modal="true">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={() => !paying && setShowBilling(false)} />
                    <div className="relative w-full max-w-[520px] max-h-[90vh] bg-[#FEFEFF] rounded-3xl shadow-[0_0_134px_14px_rgba(120,179,243,0.18)] outline outline-1 outline-[#D0E7FF] flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-5 border-b border-[#D0E7FF]">
                            <div className="flex flex-col gap-0.5">
                                <h3 className="text-[#505050] text-lg font-semibold font-heading">Billing Details</h3>
                                <span className="text-[#898989] text-xs font-medium">{credits.toLocaleString()} credits · {formatCurrency(total)}</span>
                            </div>
                            <button onClick={() => !paying && setShowBilling(false)} className="text-[#898989] hover:text-[#505050] w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F8FBFF] transition-colors" aria-label="Close">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M13 1L1 13M1 1l12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
                            <div className="flex justify-between items-center">
                                <span className="text-[#505050] text-sm font-semibold font-sans">Billing Address</span>
                                {billingForm?.address_line1 && (
                                    <button onClick={() => setUseDefault(!useDefault)} className="text-[#2766AA] text-xs font-semibold hover:underline">
                                        {useDefault ? 'Use different address' : 'Use saved address'}
                                    </button>
                                )}
                            </div>

                            {useDefault && billingForm?.address_line1 ? (
                                <div className="p-4 bg-[#F8FBFF] rounded-2xl outline outline-1 outline-[#D0E7FF] flex flex-col gap-1">
                                    {billingForm.company_name && <span className="text-[#505050] text-sm font-semibold">{billingForm.company_name}</span>}
                                    <span className="text-[#737373] text-sm">{billingForm.address_line1}{billingForm.address_line2 ? `, ${billingForm.address_line2}` : ''}</span>
                                    <span className="text-[#737373] text-sm">{billingForm.city}, {billingForm.state} {billingForm.pincode}</span>
                                    <span className="text-[#737373] text-sm">{billingForm.country || 'India'}</span>
                                    {billingForm.gstin && <span className="text-[#2766AA] text-xs font-semibold mt-1.5 uppercase tracking-wide">GSTIN: {billingForm.gstin}</span>}
                                </div>
                            ) : (
                                <div className="flex flex-col gap-3">
                                    <div>
                                        <label className="text-[#737373] text-xs font-semibold uppercase tracking-wide mb-1.5 block">Company / Business Name <span className="text-red-400">*</span></label>
                                        <input value={newAddr.company_name} onChange={e => setNewAddr(p => ({ ...p, company_name: e.target.value }))}
                                            placeholder="Enter company or business name" className="w-full px-3 py-2.5 text-sm rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3] bg-white transition-colors" />
                                    </div>
                                    <div>
                                        <label className="text-[#737373] text-xs font-semibold uppercase tracking-wide mb-1.5 block">GSTIN <span className="text-[#898989] font-normal normal-case tracking-normal">(optional)</span></label>
                                        <input value={newAddr.gstin} onChange={e => setNewAddr(p => ({ ...p, gstin: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15) }))}
                                            placeholder="e.g. 29ABCDE1234F1Z5" maxLength={15} className="w-full px-3 py-2.5 text-sm rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3] bg-white transition-colors" />
                                    </div>
                                    <div>
                                        <label className="text-[#737373] text-xs font-semibold uppercase tracking-wide mb-1.5 block">Address Line 1 <span className="text-red-400">*</span></label>
                                        <input value={newAddr.address_line1} onChange={e => setNewAddr(p => ({ ...p, address_line1: e.target.value }))}
                                            placeholder="Street address, P.O. box" className="w-full px-3 py-2.5 text-sm rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3] bg-white transition-colors" />
                                    </div>
                                    <div>
                                        <label className="text-[#737373] text-xs font-semibold uppercase tracking-wide mb-1.5 block">Address Line 2 <span className="text-[#898989] font-normal normal-case tracking-normal">(optional)</span></label>
                                        <input value={newAddr.address_line2} onChange={e => setNewAddr(p => ({ ...p, address_line2: e.target.value }))}
                                            placeholder="Apartment, suite, unit, building, floor" className="w-full px-3 py-2.5 text-sm rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3] bg-white transition-colors" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-[#737373] text-xs font-semibold uppercase tracking-wide mb-1.5 block">City <span className="text-red-400">*</span></label>
                                            <input value={newAddr.city} onChange={e => setNewAddr(p => ({ ...p, city: e.target.value }))}
                                                placeholder="City" className="w-full px-3 py-2.5 text-sm rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3] bg-white transition-colors" />
                                        </div>
                                        <div>
                                            <label className="text-[#737373] text-xs font-semibold uppercase tracking-wide mb-1.5 block">State <span className="text-red-400">*</span></label>
                                            <input value={newAddr.state} onChange={e => setNewAddr(p => ({ ...p, state: e.target.value }))}
                                                placeholder="State / Province" className="w-full px-3 py-2.5 text-sm rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3] bg-white transition-colors" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-[#737373] text-xs font-semibold uppercase tracking-wide mb-1.5 block">Postal Code <span className="text-red-400">*</span></label>
                                            <input value={newAddr.pincode} onChange={e => setNewAddr(p => ({ ...p, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                                                placeholder="560001" maxLength={6} className="w-full px-3 py-2.5 text-sm rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3] bg-white transition-colors" />
                                        </div>
                                        <div>
                                            <label className="text-[#737373] text-xs font-semibold uppercase tracking-wide mb-1.5 block">Country</label>
                                            <input value={newAddr.country} onChange={e => setNewAddr(p => ({ ...p, country: e.target.value }))}
                                                placeholder="India" className="w-full px-3 py-2.5 text-sm rounded-lg border border-[#D0E7FF] outline-none focus:border-[#78B3F3] bg-white transition-colors" />
                                        </div>
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer mt-1">
                                        <input type="checkbox" checked={setAsDefault} onChange={e => setSetAsDefault(e.target.checked)}
                                            className="w-3.5 h-3.5 accent-[#2766AA] rounded" />
                                        <span className="text-[#737373] text-sm font-medium">Save as default billing address</span>
                                    </label>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-[#D0E7FF] flex flex-col gap-2 bg-[#FEFEFF]">
                            <button onClick={handlePayment} disabled={paying || !hasBillingAddr} className="w-full h-12 px-5 bg-gradient-to-r from-[#2766AA] to-[#0752B0] rounded-xl flex justify-center items-center cursor-pointer hover:opacity-90 transition-opacity duration-200 disabled:opacity-50">
                                <span className="text-white text-base font-semibold font-sans">{paying ? 'Processing...' : `Pay ${formatCurrency(total)}`}</span>
                            </button>
                            <span className="text-[#737373] text-[11px] font-medium font-sans text-center">
                                Prices inclusive of 18% GST. One-time charge, no recurring billing.
                            </span>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {showCoupons && createPortal(
                <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" role="dialog" aria-modal="true">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={() => setShowCoupons(false)} />
                    <div className="relative w-full max-w-[520px] max-h-[88vh] bg-white rounded-3xl shadow-[4px_46px_134px_14px_rgba(96,146,200,0.15)] outline outline-1 outline-[#D0E7FF] flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="flex items-start justify-between px-6 pt-6 pb-1">
                            <div className="flex flex-col mb-4">
                                <h3 className="text-[#3A3A3A] text-2xl font-semibold font-sans leading-none mb-0.5">Available Coupons</h3>
                                <span className="text-[#737373] text-sm font-normal font-sans mt-1">Sorted by maximum savings · {shapedCoupons.length} offers</span>
                            </div>
                            <button onClick={() => setShowCoupons(false)} className="text-[#505050] hover:text-[#3A3A3A] w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F8FBFF] transition-colors shrink-0" aria-label="Close">
                                <svg width="15" height="15" viewBox="0 0 14 14" fill="none"><path d="M13 1L1 13M1 1l12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                            </button>
                        </div>

                        {/* Coupon list */}
                        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-3 flex flex-col gap-4">
                            {shapedCoupons.length === 0 && (
                                <div className="text-center text-[#737373] text-sm py-8">No coupons available right now.</div>
                            )}
                            {shapedCoupons.map((c) => {
                                const isApplied = appliedCoupon === c.code;
                                return (
                                    <div key={c.code} className="relative flex items-stretch rounded-2xl shadow-[4px_46px_134px_14px_rgba(96,146,200,0.10)] overflow-hidden">
                                        {/* Discount band */}
                                        <div className="w-[72px] shrink-0 bg-[#2766AA] flex items-center justify-center">
                                            <span className="text-white text-xl font-bold font-sans whitespace-nowrap -rotate-90">{c.offLabel}</span>
                                        </div>
                                        {/* Ticket notch: one semi-circle on the left edge, vertically centered */}
                                        <span className="absolute left-0 top-1/2 w-6 h-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
                                        {/* Body */}
                                        <div className="flex-1 min-w-0 bg-white p-3.5 flex flex-col justify-center gap-1">
                                            <div className="flex items-center gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[#34A754] text-base font-semibold font-sans">You save ₹{c.save}</div>
                                                    <div className="text-[#3A3A3A] text-lg font-bold font-sans truncate">{c.code}</div>
                                                </div>
                                                {isApplied ? (
                                                    <div className="h-11 px-5 bg-[#78B3F3] rounded-lg flex items-center justify-center gap-2 shrink-0">
                                                        <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M3 8.5l3.5 3.5L13 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                        <span className="text-white text-sm font-semibold font-sans">Applied</span>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => { setShowCoupons(false); applyCouponCode(c.code); }}
                                                        className="h-11 px-5 rounded-lg outline outline-[1.5px] outline-offset-[-1.5px] outline-[#2766AA] flex items-center justify-center text-[#2766AA] text-sm font-semibold font-sans hover:opacity-90 transition-opacity cursor-pointer shrink-0"
                                                    >
                                                        Apply
                                                    </button>
                                                )}
                                            </div>
                                            <div className="text-[#505050] text-sm font-normal font-sans">{c.condition}</div>
                                            {c.bestDeal && (
                                                <div className="self-start px-2 py-0.5 bg-[#CFE9FE]/20 rounded-full outline outline-1 outline-offset-[-1px] outline-[#78B3F3] flex items-center gap-1.5">
                                                    <img src={starIcon} alt="" className="w-3.5 h-3.5" />
                                                    <span className="text-[#2766AA]/70 text-xs font-semibold font-sans">Best Deal</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default CreditSlider;
